import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import PageAdvice from "../components/PageAdvice";
import { IntegrationConnectionBar } from "../components/IntegrationConnectionBar";
import {
  CircleNotch, FloppyDisk, CheckCircle, Info, Plugs, Warning, ArrowsLeftRight,
  Cube, ClipboardText, ShoppingCart, Database, MagnifyingGlass,
  Funnel, ListBullets, Code, ShieldCheck, Lightning, Globe, ArrowDown, ArrowUp,
  PlugsConnected, CaretLeft, CaretRight, XCircle,
} from "@phosphor-icons/react";

const TABS = [
  { id: "overview", label: "Overview", icon: Info },
  { id: "options", label: "API Options", icon: ListBullets },
  { id: "connection", label: "Connection", icon: Plugs },
  { id: "assets", label: "Assets", icon: Cube },
  { id: "workorders", label: "Work Orders", icon: ClipboardText },
  { id: "directory", label: "Asset Directory", icon: Database },
];

const FACTS = [
  { icon: Globe, title: "OData / RESTful", body: "Open Data Protocol over HTTPS — standard REST verbs and query syntax. Works from any HTTP client." },
  { icon: Database, title: "Built into MEX 15", body: "Exposed at the relative path /OData.svc/ on your MEX site. No additional licensing required." },
  { icon: Code, title: "JSON or XML", body: "Defaults to XML; set the Accept & Content-Type headers to application/json for readable JSON." },
  { icon: ShieldCheck, title: "Basic authentication", body: "When MEX security is on, supply MEX user credentials via an Authorization: Basic header (admin user recommended)." },
];

// Entities/resources the MEX OData service exposes. The full live list is
// discoverable at the service root (/OData.svc/) and $metadata.
const RESOURCES = [
  { icon: Cube, name: "Assets", path: "/Assets", desc: "Asset register — hierarchy (ParentAssetID), AssetNumber, AssetDescription, AssetTypeID, AccountCodeID, IsAsset / IsActive flags.", use: "Mirror the MEX asset register into Asset Hub; align with AroFlo / Maximo assets." },
  { icon: ClipboardText, name: "Work Orders", path: "/WorkOrders", desc: "Work orders — WorkOrderID, WorkOrderDescription, RaisedDateTime, status & cost fields.", use: "Detect & ingest maintenance work orders; sync status, logs and actuals." },
  { icon: ShoppingCart, name: "Purchase Orders", path: "/PurchaseOrders", desc: "Purchasing records associated with work / assets.", use: "Reconcile materials & costs against jobs (future)." },
  { icon: Database, name: "Other entities", path: "/OData.svc/", desc: "Locations, contacts, account codes, asset types and more are exposed as additional OData entity sets.", use: "Browse the service root for the full list available on your MEX instance." },
];

// OData operations available against each resource.
const OPERATIONS = [
  { verb: "GET", dir: "pull", name: "List a collection", ex: "GET /OData.svc/Assets", note: "Returns all records in an entity set." },
  { verb: "GET", dir: "pull", name: "Read by key (ID)", ex: "GET /OData.svc/Assets(75)", note: "Fetch a single record by its primary key." },
  { verb: "GET", dir: "pull", name: "Filter records", ex: "GET /OData.svc/Assets?$filter=AssetNumber eq 'ADMIN'", note: "OData $filter for server-side querying." },
  { verb: "GET", dir: "pull", name: "Select / shape fields", ex: "GET /OData.svc/WorkOrders?$select=WorkOrderID,WorkOrderDescription", note: "Combine with $filter, $top, $skip, $orderby, $expand." },
  { verb: "GET", dir: "meta", name: "Service root", ex: "GET /OData.svc/", note: "Lists every available resource/entity name." },
  { verb: "GET", dir: "meta", name: "Metadata schema", ex: "GET /OData.svc/$metadata", note: "Full entity model — fields, types and relationships." },
  { verb: "POST", dir: "push", name: "Create a record", ex: "POST /OData.svc/Assets  { … }", note: "Create e.g. a new asset or work order." },
  { verb: "PATCH", dir: "push", name: "Update a record", ex: "PATCH /OData.svc/Assets(201)  { … }", note: "Partial update of an existing record." },
  { verb: "DELETE", dir: "push", name: "Delete a record", ex: "DELETE /OData.svc/Assets(201)", note: "Remove a record by key." },
];

const QUERY_OPTS = ["$filter", "$select", "$top", "$skip", "$orderby", "$expand", "$count"];

const DIR_STYLE = {
  pull: { label: "Read", cls: "bg-[#0055FF]/10 text-[#0055FF]", icon: ArrowDown },
  push: { label: "Write", cls: "bg-[#D97706]/10 text-[#D97706]", icon: ArrowUp },
  meta: { label: "Schema", cls: "bg-neutral-200 text-neutral-600", icon: Code },
};
const VERB_STYLE = {
  GET: "bg-[#059669]/10 text-[#059669]",
  POST: "bg-[#0055FF]/10 text-[#0055FF]",
  PATCH: "bg-[#D97706]/10 text-[#D97706]",
  DELETE: "bg-[#DC2626]/10 text-[#DC2626]",
};

const EXAMPLES = [
  { label: "Get the list of available resources", code: 'curl -L "{BASE}/OData.svc/" --header "Accept: application/json"' },
  { label: "Get all assets (JSON)", code: 'curl "{BASE}/OData.svc/Assets" --header "Accept: application/json"' },
  { label: "Get an asset by number", code: 'curl -G "{BASE}/OData.svc/Assets" --data-urlencode "$filter=AssetNumber eq \'ADMIN\'" --header "Accept: application/json"' },
  { label: "Work orders raised after a date", code: 'curl -G "{BASE}/OData.svc/WorkOrders" --data-urlencode "$select=WorkOrderID,WorkOrderDescription" --data-urlencode "$filter=RaisedDateTime gt DateTime\'2026-01-01T00:00:00\'" --header "Accept: application/json"' },
  { label: "Authenticated request (Basic auth)", code: 'curl -L "{BASE}/OData.svc/" -u admin:admin --header "Accept: application/json"' },
];

const inputCls = "w-full rounded-sm border border-neutral-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0055FF]";

export default function MexIntegration() {
  const [config, setConfig] = useState({ connection: {} });
  const [tab, setTab] = useState("overview");
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [connId, setConnId] = useState("");

  useEffect(() => {
    if (!connId) return;
    setTestResult(null);
    api.get("/mex/config", { params: { connection_id: connId } }).then((r) => setConfig(r.data)).catch(() => setConfig({ connection: {} }));
  }, [connId]);

  const setConn = (k, v) => setConfig((p) => ({ ...p, connection: { ...p.connection, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/mex/config", { connection: config.connection }, { params: { connection_id: connId } });
      setConfig(r.data);
      setFlash(true); setTimeout(() => setFlash(false), 2200);
      toast.success("MEX connection saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/mex/test-connection", null, { params: { connection_id: connId } });
      setTestResult(data);
      if (data.connected) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      setTestResult({ connected: false, status: "error", message: msg });
      toast.error(msg);
    } finally { setTesting(false); }
  };

  const conn = config.connection || {};
  const base = conn.base_url ? conn.base_url.replace(/\/?OData\.svc\/?$/i, "") : "{your-mex-url}";

  return (
    <Layout>
      <div data-testid="mex-page">
        <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Integration · Planning</p>
        <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">MEX Integration</h1>
        <p className="mt-2 max-w-3xl text-base text-neutral-600">
          Connect Asset Hub to <span className="font-semibold">MEX (Maintenance Experts)</span> via the MEX OData API — a
          RESTful interface to MEX work orders, assets and purchase orders.
        </p>

        <div className="mt-4 flex items-center gap-2 rounded-sm border border-[#D97706]/30 bg-[#D97706]/5 px-4 py-2.5" data-testid="mex-status-banner">
          <Warning size={18} weight="fill" className="text-[#D97706]" />
          <span className="text-sm text-neutral-700">Planning &amp; connection setup only — <span className="font-semibold">no live sync is active yet</span>. Saved credentials are stored for later; no requests are made to MEX.</span>
        </div>

        {/* Tabs */}
        <div className="mt-6">
          <IntegrationConnectionBar provider="mex" value={connId} onChange={setConnId} label="MEX client connection" />
        </div>
        <div className="inline-flex flex-wrap gap-1 rounded-sm border border-neutral-200 bg-white p-1 shadow-sm">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`mex-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 rounded-sm px-4 py-2 text-sm font-semibold transition-all duration-200 ${tab === t.id ? "bg-[#0A0A0A] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>
              <t.icon size={16} weight="bold" />{t.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW */}
        {tab === "overview" && (
          <div className="mt-6 space-y-6" data-testid="mex-overview">
            <div className="grid gap-4 sm:grid-cols-2">
              {FACTS.map((f) => (
                <div key={f.title} className="rounded-sm border border-neutral-200 bg-white p-5 shadow-sm">
                  <p className="flex items-center gap-2 font-semibold text-[#0A0A0A]"><f.icon size={18} weight="bold" className="text-[#0055FF]" />{f.title}</p>
                  <p className="mt-1.5 text-sm text-neutral-600">{f.body}</p>
                </div>
              ))}
            </div>

            <section className="rounded-sm border border-neutral-200 bg-white p-6 shadow-sm">
              <h2 className="flex items-center gap-2 text-lg font-bold text-[#0A0A0A]"><Lightning size={18} weight="bold" className="text-[#0055FF]" /> What we can build</h2>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {[
                  "Pull the MEX asset register into Asset Hub and reconcile with AroFlo / Maximo assets.",
                  "Detect and ingest MEX work orders, then sync status, work logs and actual costs.",
                  "Mirror condition / compliance results back to the MEX asset register (write-back).",
                  "Reconcile purchase orders, materials and costs against jobs.",
                ].map((x) => <li key={x} className="flex gap-2 text-sm text-neutral-600"><CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-[#059669]" />{x}</li>)}
              </ul>
            </section>

            <section className="rounded-sm border border-neutral-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-bold text-[#0A0A0A]">Reference</h2>
              <ul className="mt-3 space-y-2 text-sm text-neutral-600">
                <li className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0055FF]" /><span>Service endpoint: <code className="rounded-sm bg-neutral-100 px-1.5 py-0.5 text-xs">{`{your-mex-url}`}/OData.svc/</code></span></li>
                <li className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0055FF]" /><span>Docs: <a href="https://mex.zendesk.com/hc/en-us/articles/42715011963789-Using-the-MEX-OData-API" target="_blank" rel="noreferrer" className="font-semibold text-[#0055FF] hover:underline">Using the MEX OData API</a></span></li>
                <li className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0055FF]" /><span>.NET client &amp; wiki: <a href="https://github.com/MaintenanceExperts/MEXODataAPI" target="_blank" rel="noreferrer" className="font-semibold text-[#0055FF] hover:underline">MaintenanceExperts/MEXODataAPI</a></span></li>
              </ul>
            </section>
          </div>
        )}

        {/* API OPTIONS */}
        {tab === "options" && (
          <div className="mt-6 space-y-6" data-testid="mex-options">
            {/* Resources */}
            <section>
              <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-[#0A0A0A]"><Database size={18} weight="bold" className="text-[#0055FF]" /> Resources you can connect to</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {RESOURCES.map((r) => (
                  <div key={r.name} className="rounded-sm border border-neutral-200 bg-white p-5 shadow-sm" data-testid={`mex-resource-${r.name}`}>
                    <div className="flex items-center justify-between">
                      <p className="flex items-center gap-2 font-semibold text-[#0A0A0A]"><r.icon size={18} weight="bold" className="text-[#0055FF]" />{r.name}</p>
                      <code className="rounded-sm bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">{r.path}</code>
                    </div>
                    <p className="mt-2 text-sm text-neutral-600">{r.desc}</p>
                    <p className="mt-2 flex gap-1.5 text-xs text-neutral-500"><ArrowsLeftRight size={13} className="mt-0.5 shrink-0 text-[#059669]" /><span><span className="font-semibold">Asset Hub use:</span> {r.use}</span></p>
                  </div>
                ))}
              </div>
            </section>

            {/* Operations */}
            <section>
              <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-[#0A0A0A]"><Funnel size={18} weight="bold" className="text-[#0055FF]" /> Operations available per resource</h2>
              <div className="overflow-hidden rounded-sm border border-neutral-200 bg-white shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead><tr className="border-b border-neutral-200 bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
                    <th className="px-4 py-3 font-bold">Method</th><th className="px-4 py-3 font-bold">Type</th><th className="px-4 py-3 font-bold">Operation</th><th className="px-4 py-3 font-bold">Example</th><th className="px-4 py-3 font-bold">Notes</th>
                  </tr></thead>
                  <tbody>
                    {OPERATIONS.map((op) => {
                      const d = DIR_STYLE[op.dir];
                      return (
                        <tr key={op.name} className="border-b border-neutral-100 last:border-0" data-testid={`mex-op-${op.name}`}>
                          <td className="px-4 py-3"><span className={`rounded-sm px-2 py-0.5 text-xs font-bold ${VERB_STYLE[op.verb]}`}>{op.verb}</span></td>
                          <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${d.cls}`}><d.icon size={11} weight="bold" />{d.label}</span></td>
                          <td className="px-4 py-3 font-medium text-[#0A0A0A]">{op.name}</td>
                          <td className="px-4 py-3"><code className="rounded-sm bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700">{op.ex}</code></td>
                          <td className="px-4 py-3 text-neutral-500">{op.note}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-neutral-400">Write operations (POST / PATCH / DELETE) stay disabled until go-live access is agreed. Asset Hub will start with read-only pulls.</p>
            </section>

            {/* Query options */}
            <section className="rounded-sm border border-neutral-200 bg-white p-6 shadow-sm">
              <h2 className="flex items-center gap-2 text-lg font-bold text-[#0A0A0A]"><MagnifyingGlass size={18} weight="bold" className="text-[#0055FF]" /> OData query options</h2>
              <p className="mt-1 text-sm text-neutral-500">Standard OData system query options can be combined on any GET request for server-side filtering, shaping and paging.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {QUERY_OPTS.map((q) => <code key={q} className="rounded-sm bg-[#0055FF]/10 px-2.5 py-1 text-xs font-semibold text-[#0055FF]">{q}</code>)}
              </div>
            </section>

            {/* Examples */}
            <section>
              <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-[#0A0A0A]"><Code size={18} weight="bold" className="text-[#0055FF]" /> Example requests</h2>
              <div className="space-y-3">
                {EXAMPLES.map((ex) => (
                  <div key={ex.label} className="overflow-hidden rounded-sm border border-neutral-200 bg-white shadow-sm">
                    <div className="border-b border-neutral-200 bg-neutral-50 px-4 py-2 text-xs font-semibold text-neutral-600">{ex.label}</div>
                    <pre className="overflow-x-auto px-4 py-3 text-xs text-neutral-800"><code>{ex.code.replace("{BASE}", base)}</code></pre>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {/* CONNECTION */}
        {tab === "connection" && (
          <div className="mt-6 max-w-2xl space-y-5 rounded-sm border border-neutral-200 bg-white p-6 shadow-sm" data-testid="mex-connection">
            <div className="flex items-center gap-2">
              <span className="rounded-sm bg-neutral-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Status: not connected</span>
              <span className="text-xs text-neutral-400">Saved for later — no requests are made.</span>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-neutral-700">MEX OData service URL</label>
              <input data-testid="mex-base-url" value={conn.base_url || ""} onChange={(e) => setConn("base_url", e.target.value)}
                placeholder="https://yoursite.mex.com.au/MEX/OData.svc" className={inputCls} spellCheck={false} />
              <p className="mt-1 text-xs text-neutral-400">The service root, ending in <code className="rounded-sm bg-neutral-100 px-1 text-[11px]">/OData.svc</code>.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="mb-1.5 block text-sm font-semibold text-neutral-700">Username</label>
                <input data-testid="mex-username" value={conn.username || ""} onChange={(e) => setConn("username", e.target.value)} placeholder="MEX user (admin)" className={inputCls} /></div>
              <div><label className="mb-1.5 block text-sm font-semibold text-neutral-700">Password</label>
                <input data-testid="mex-password" type="password" value={conn.password || ""} onChange={(e) => setConn("password", e.target.value)}
                  placeholder={conn.password_set ? "•••••••• (saved)" : "MEX password"} className={inputCls} /></div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-neutral-700">Response format</label>
              <select data-testid="mex-format" value={conn.response_format || "json"} onChange={(e) => setConn("response_format", e.target.value)} className={inputCls}>
                <option value="json">JSON (Accept: application/json)</option>
                <option value="xml">XML (OData default)</option>
              </select>
            </div>
            <div className="flex items-center gap-2 rounded-sm bg-[#0055FF]/5 px-3 py-2 text-xs text-neutral-600">
              <ShieldCheck size={15} weight="fill" className="shrink-0 text-[#0055FF]" />
              Authentication uses HTTP Basic (username + password, base64-encoded) per the MEX OData API. Credentials are stored securely and never echoed back.
            </div>
            <div className="flex items-center justify-between border-t border-neutral-200 pt-4">
              <button data-testid="mex-test-btn" onClick={testConnection} disabled={testing}
                className="inline-flex items-center gap-2 rounded-sm border border-[#0055FF] px-4 py-2 text-sm font-semibold text-[#0055FF] hover:bg-[#0055FF]/5 disabled:opacity-60">
                {testing ? <CircleNotch size={15} className="animate-spin" /> : <PlugsConnected size={15} weight="bold" />} Test connection
              </button>
              <button data-testid="mex-save-btn" onClick={save} disabled={saving}
                className="inline-flex items-center gap-2 rounded-sm bg-[#059669] px-4 py-2 text-sm font-semibold text-white hover:bg-[#047857] disabled:opacity-60">
                {saving ? <CircleNotch size={15} className="animate-spin" /> : flash ? <CheckCircle size={15} weight="fill" /> : <FloppyDisk size={15} weight="bold" />}
                {flash ? "Saved" : "Save connection"}
              </button>
            </div>

            {testResult && (
              <div data-testid="mex-test-result" className={`rounded-sm border px-4 py-3 text-sm ${testResult.connected ? "border-[#059669]/30 bg-[#059669]/5" : "border-[#DC2626]/30 bg-[#DC2626]/5"}`}>
                <p className="flex items-center gap-2 font-semibold">
                  {testResult.connected ? <CheckCircle size={16} weight="fill" className="text-[#059669]" /> : <XCircle size={16} weight="fill" className="text-[#DC2626]" />}
                  <span className={testResult.connected ? "text-[#059669]" : "text-[#DC2626]"}>{testResult.message}</span>
                </p>
                {testResult.connected && testResult.resources?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Discovered resources ({testResult.resource_count})</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {testResult.resources.map((r) => <code key={r} className="rounded-sm bg-white px-2 py-0.5 text-xs text-neutral-700 ring-1 ring-neutral-200">{r}</code>)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* LIVE DATA */}
        {tab === "assets" && <LiveData entityId="assets" connId={connId} />}
        {tab === "workorders" && <LiveData entityId="workorders" connId={connId} />}

        {/* SYNCED ASSET DIRECTORY */}
        {tab === "directory" && <AssetDirectory connId={connId} />}

        <PageAdvice title="Important — planning & discovery only" items={[
          "This page documents the MEX OData API options and stores connection details. It does not connect to MEX yet.",
          "The full list of available resources is discoverable live at your service root (/OData.svc/) and /$metadata once connected.",
          "Live MEX sync (Phase 2) is blocked pending a MEX URL, API credentials and agreed read/write scope.",
        ]} />
      </div>
    </Layout>
  );
}

const ENTITIES = [
  { id: "assets", label: "Assets", path: "/mex/assets", icon: Cube, ph: "Search asset number or description…" },
  { id: "workorders", label: "Work Orders", path: "/mex/workorders", icon: ClipboardText, ph: "Search work order description…" },
];

function LiveData({ entityId, connId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const ent = ENTITIES.find((e) => e.id === entityId);

  useEffect(() => { const t = setTimeout(() => setDebounced(search), 400); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); setSearch(""); }, [entityId, connId]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(ent.path, { params: { page, limit: 25, search: debounced, connection_id: connId } });
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
      setData(null);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [entityId, page, debounced, connId]);

  const cols = data?.columns || [];
  const total = data?.count;
  const pages = total != null ? Math.max(1, Math.ceil(total / 25)) : null;

  return (
    <div className="mt-6 space-y-4" data-testid="mex-live">
      <div className="flex items-center gap-2 rounded-sm border border-[#0055FF]/30 bg-[#0055FF]/5 px-4 py-2.5">
        <Info size={18} weight="fill" className="text-[#0055FF]" />
        <span className="text-sm text-neutral-700"><span className="font-semibold">Read-only.</span> Live {ent.label.toLowerCase()} fetched from MEX over OData (GET only). Nothing is ever written to MEX.</span>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <div className="relative w-full max-w-xs">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input data-testid="mex-live-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={ent.ph}
              className="w-full rounded-sm border border-neutral-300 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#0055FF]" />
          </div>
          <button data-testid="mex-live-refresh" onClick={load} disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 hover:border-[#0055FF] disabled:opacity-50">
            <ArrowsLeftRight size={15} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-sm border border-neutral-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
        ) : error ? (
          <div className="flex h-64 flex-col items-center justify-center gap-2 px-6 text-center text-sm text-[#D97706]" data-testid="mex-live-error">
            <Warning size={28} weight="fill" /> {error}
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center gap-2 text-neutral-400" data-testid="mex-live-empty">
            <ent.icon size={30} /> No {ent.label.toLowerCase()} returned.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm" data-testid="mex-live-table">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
                  {cols.map((c) => <th key={c} className="whitespace-nowrap px-4 py-2.5 text-left font-bold">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.items.map((row, i) => (
                  <tr key={i} className="border-b border-neutral-100 last:border-0 even:bg-neutral-50/50" data-testid={`mex-live-row-${i}`}>
                    {cols.map((c) => <td key={c} className="whitespace-nowrap px-4 py-2.5 text-neutral-700">{row[c] == null || row[c] === "" ? "—" : String(row[c])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data && data.items.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-500" data-testid="mex-live-count">
            {total != null ? `${total.toLocaleString()} ${ent.label.toLowerCase()}` : `${data.returned} shown`}{pages ? ` · page ${page} of ${pages}` : ""}
          </p>
          <div className="flex gap-2">
            <button data-testid="mex-live-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              className="inline-flex items-center gap-1 rounded-sm border border-neutral-300 px-3 py-1.5 text-sm font-semibold disabled:opacity-40"><CaretLeft size={14} weight="bold" /> Prev</button>
            <button data-testid="mex-live-next" disabled={pages != null ? page >= pages : data.returned < 25} onClick={() => setPage((p) => p + 1)}
              className="inline-flex items-center gap-1 rounded-sm border border-neutral-300 px-3 py-1.5 text-sm font-semibold disabled:opacity-40">Next <CaretRight size={14} weight="bold" /></button>
          </div>
        </div>
      )}
    </div>
  );
}


function AssetDirectory({ connId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [page, setPage] = useState(1);

  useEffect(() => { const t = setTimeout(() => setDebounced(search), 400); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [debounced, statusFilter, connId]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mex/assets/directory", { params: { page, limit: 50, search: debounced, status: statusFilter, connection_id: connId } });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page, debounced, statusFilter, connId]);

  const poll = async () => {
    try {
      const { data: st } = await api.get("/mex/assets/sync/status", { params: { connection_id: connId } });
      if (st.running) { setTimeout(poll, 2500); return; }
      setSyncing(false);
      if (st.last_error) toast.error(st.last_error);
      else toast.success(`MEX assets synced — ${st.total_active ?? st.active_count ?? 0} active`);
      load();
    } catch {
      setSyncing(false);
      load();
    }
  };

  const startSync = async () => {
    setSyncing(true);
    try {
      await api.post("/mex/assets/sync", null, { params: { connection_id: connId } });
      toast.info("MEX asset sync started…");
      setTimeout(poll, 2000);
    } catch (e) {
      setSyncing(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const items = data?.items || [];
  const pages = data?.pages || 1;

  return (
    <div className="mt-6 space-y-4" data-testid="mex-directory">
      <div className="flex items-center gap-2 rounded-sm border border-[#0055FF]/30 bg-[#0055FF]/5 px-4 py-2.5">
        <Database size={18} weight="fill" className="text-[#0055FF]" />
        <span className="text-sm text-neutral-700"><span className="font-semibold">Cached directory.</span> Sync pulls the full MEX asset register (read-only) into Asset Hub so it loads instantly and can feed dashboards. Vanished assets are flagged removed, never deleted.</span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2" data-testid="mex-dir-filters">
          {["active", "removed", "all"].map((s) => (
            <button key={s} data-testid={`mex-dir-filter-${s}`} onClick={() => setStatusFilter(s)}
              className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold capitalize transition-all duration-200 ${statusFilter === s ? "border-[#0055FF] bg-[#0055FF] text-white" : "border-neutral-300 bg-white text-neutral-700 hover:border-[#0055FF]"}`}>
              {s}{s === "active" && data?.active_count != null ? ` ${data.active_count}` : ""}{s === "removed" && data?.removed_count ? ` ${data.removed_count}` : ""}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-full max-w-xs">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input data-testid="mex-dir-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search asset number or description…"
              className="w-full rounded-sm border border-neutral-300 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#0055FF]" />
          </div>
          <button data-testid="mex-dir-sync" onClick={startSync} disabled={syncing}
            className="inline-flex items-center gap-1.5 rounded-sm bg-[#0A0A0A] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0055FF] disabled:opacity-50">
            {syncing ? <CircleNotch size={15} className="animate-spin" /> : <ArrowsLeftRight size={15} weight="bold" />} {syncing ? "Syncing…" : "Sync from MEX"}
          </button>
        </div>
      </div>

      {data?.last_synced && (
        <p className="text-xs text-neutral-400" data-testid="mex-dir-synced">Last synced {new Date(data.last_synced).toLocaleString()}{data.last_error ? ` · last error: ${data.last_error}` : ""}</p>
      )}

      <div className="overflow-hidden rounded-sm border border-neutral-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
        ) : items.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center gap-2 px-6 text-center text-neutral-400" data-testid="mex-dir-empty">
            <Cube size={30} />
            <p className="text-sm font-medium text-neutral-500">No MEX assets cached yet.</p>
            <p className="max-w-md text-xs text-neutral-400">Save your MEX service URL + credentials on the Connection tab, then press "Sync from MEX" to import the asset register.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm" data-testid="mex-dir-table">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
                  <th className="px-4 py-2.5 text-left font-bold">Asset #</th>
                  <th className="px-4 py-2.5 text-left font-bold">Description</th>
                  <th className="px-4 py-2.5 text-left font-bold">Status</th>
                  <th className="px-4 py-2.5 text-left font-bold">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.mex_key} data-testid={`mex-dir-row-${a.mex_key}`} className={`border-b border-neutral-100 last:border-0 even:bg-neutral-50/50 ${a.status === "removed" ? "opacity-50" : ""}`}>
                    <td className="px-4 py-2.5 font-mono text-xs font-semibold text-[#0055FF]">{a.asset_number || "—"}</td>
                    <td className="max-w-[420px] truncate px-4 py-2.5 text-neutral-700" title={a.description}>{a.description || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex rounded-sm px-2 py-0.5 text-xs font-bold ${a.status === "removed" ? "bg-neutral-200 text-neutral-500" : "bg-[#059669]/10 text-[#059669]"}`}>{a.status === "removed" ? "Removed" : "Active"}</span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-neutral-500">{a.last_seen ? new Date(a.last_seen).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data && data.count > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-500" data-testid="mex-dir-count">{data.count.toLocaleString()} assets · page {page} of {pages}</p>
          <div className="flex gap-2">
            <button data-testid="mex-dir-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              className="inline-flex items-center gap-1 rounded-sm border border-neutral-300 px-3 py-1.5 text-sm font-semibold disabled:opacity-40"><CaretLeft size={14} weight="bold" /> Prev</button>
            <button data-testid="mex-dir-next" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
              className="inline-flex items-center gap-1 rounded-sm border border-neutral-300 px-3 py-1.5 text-sm font-semibold disabled:opacity-40">Next <CaretRight size={14} weight="bold" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
